import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Link as RouterLink,
  useNavigate,
  useLocation
} from 'react-router-dom';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

// Icons for tab navigation
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArticleIcon from '@mui/icons-material/Article';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PublicIcon from '@mui/icons-material/Public';

// Import navigation context
import { NavigationProvider, useNavigation } from './context/NavigationContext';

// Import auth
import { ProtectedRoute } from './auth';

// Import components
import LearnHub from './components/LearnHub';
import FinancialStatements from './components/FinancialStatements';
import MetricTable from './components/MetricTable';
import CryptoAnalysis from './components/CryptoAnalysis';

import BloombergLive from './components/CNBCLive';
import Header from './components/Layout/Header';
import CNBCLive from './components/CNBCLive';
import CompanyDashboard from './components/CompanyDashboard';
import VisualizationGuide from './components/VisualizationGuide';
import CompanyComparison from './components/CompanyComparison';
import Watchlist from './components/Watchlist';
import CountryTickerMap from './components/CountryTickerMap';
import PaperTrading from './components/PaperTrading';
import PaperAccountSummary from './components/PaperAccountSummary';
import AccountSettings from './components/AccountSettings';
import EconomicCalendar from './components/EconomicCalendar';
import FedRates from './components/FedRates';
import MoneySupply from './components/MoneySupply';
import EarningsCalendarTab from './components/EarningsCalendarTab';
import RouteErrorBoundary from './components/Common/RouteErrorBoundary';
import { TermsPage, PrivacyPage, CookiePolicyPage } from './components/LegalPages';

// API Configuration from environment variables
// Empty string means same-origin (nginx proxies /api/ to backend)
const API_CONFIG = {
  API_BASE_URL: process.env.REACT_APP_API_BASE_URL ?? '',
  PG_DATABASE: 'rasppiprod', 
  PG_PORT: 5432
};

const SELECTED_TICKER_STORAGE_KEY = 'selectedTicker';
const SELECTED_EXCHANGE_STORAGE_KEY = 'selectedExchange';
const COMPANY_ANALYSIS_ROUTES = new Set([
  '/',
  '/dashboard',
  '/financial-statements',
  '/metric-table',
  '/visualization-guide',
  '/thesis',
  '/alerts',
  '/paper-trading',
  '/company-comparison',
  '/earnings-calendar',
]);

const WATCHLIST_SIDEBAR_HIDDEN_ROUTES = new Set([
  '/visualization-guide',
  '/company-comparison',
]);

const sharedTabSx = {
  minHeight: { xs: 36, sm: 48 },
  minWidth: { xs: 'auto', sm: 140 },
  px: { xs: 1, sm: 2 },
  fontSize: { xs: '0.7rem', sm: '0.9rem' },
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
};

const getTheme = (mode) => createTheme({
  palette: {
    mode,
    primary: {
      main: '#2563eb',
      light: '#3b82f6',
      dark: '#1d4ed8',
    },
    secondary: {
      main: mode === 'dark' ? '#14b8a6' : '#7c3aed',
      light: mode === 'dark' ? '#2dd4bf' : '#8b5cf6',
      dark: mode === 'dark' ? '#0f766e' : '#6d28d9',
    },
    background: mode === 'dark'
      ? {
          default: '#0b1220',
          paper: '#121a2a',
        }
      : {
          default: '#f8fafc',
          paper: '#ffffff',
        },
    text: mode === 'dark'
      ? {
          primary: '#e2e8f0',
          secondary: '#94a3b8',
        }
      : {
          primary: '#1e293b',
          secondary: '#64748b',
        },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      letterSpacing: '-0.025em',
    },
    h6: {
      fontSize: '1.25rem',
      fontWeight: 600,
      letterSpacing: '-0.025em',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'dark' ? '#121a2a' : '#ffffff',
          boxShadow: mode === 'dark'
            ? '0 1px 3px 0 rgb(0 0 0 / 0.4)'
            : '0 1px 3px 0 rgb(0 0 0 / 0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: mode === 'dark'
            ? '0 1px 3px 0 rgb(0 0 0 / 0.4), 0 1px 2px -1px rgb(0 0 0 / 0.3)'
            : '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          borderRadius: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 48,
          fontWeight: 500,
        },
      },
    },
  },
});

// Create a wrapper component to handle tab navigation with improved organization
const TabNavigator = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isCompactLandscape = useMediaQuery('(max-height:480px) and (orientation: landscape)');
  const { 
    mainTab, 
    subTab, 
    handleMainTabChange, 
    handleCompanyAnalysisTabChange, 
    handleCryptoAnalysisTabChange,
    handleMarketNewsTabChange 
  } = useNavigation();

  return (
    <Box sx={{ px: { xs: 0.5, sm: 0 }, py: isCompactLandscape ? 0 : undefined }}>
      {/* Main Tab Navigation */}
      <Tabs
        value={mainTab}
        onChange={handleMainTabChange}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: isCompactLandscape ? 26 : { xs: 36, sm: 48 },
          '& .MuiTabs-scrollButtons': {
            width: isCompactLandscape ? 16 : { xs: 24, sm: 40 },
          },
          '& .MuiTabs-flexContainer': {
            gap: isCompactLandscape ? 0 : { xs: 0.5, sm: 1 },
          },
          '& .MuiTab-root': isCompactLandscape ? { minHeight: 26, py: 0, px: 0.75, fontSize: '0.65rem' } : {},
        }}
      >
        <Tab label="Welcome" icon={isMobile ? undefined : <InfoOutlinedIcon />} iconPosition="start" sx={sharedTabSx} />
        <Tab label={isMobile ? 'News' : 'Market News'} icon={isMobile ? undefined : <ArticleIcon />} iconPosition="start" sx={sharedTabSx} />
        <Tab label="Stocks" icon={isMobile ? undefined : <ShowChartIcon />} iconPosition="start" sx={sharedTabSx} />
        <Tab label={isMobile ? 'Crypto' : 'Crypto Analysis'} icon={isMobile ? undefined : <CurrencyBitcoinIcon />} iconPosition="start" sx={sharedTabSx} />
        <Tab label="Watchlist" icon={isMobile ? undefined : <PlaylistAddCheckIcon />} iconPosition="start" sx={sharedTabSx} />
        <Tab label="Map" icon={isMobile ? undefined : <PublicIcon />} iconPosition="start" sx={sharedTabSx} />
      </Tabs>
      
      {/* Sub-tabs for Company Analysis */}
      {mainTab === 2 && (
        <Tabs
          value={subTab}
          onChange={handleCompanyAnalysisTabChange}
          indicatorColor="secondary"
          textColor="secondary"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            mt: isCompactLandscape ? 0 : { xs: 1, sm: 2 },
            mb: isCompactLandscape ? 0 : 1,
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: isCompactLandscape ? 24 : { xs: 36, sm: 48 },
            '& .MuiTabs-scrollButtons': {
              width: isCompactLandscape ? 16 : { xs: 24, sm: 40 },
            },
            '& .MuiTabs-flexContainer': {
              gap: isCompactLandscape ? 0 : { xs: 0.5, sm: 0.75 },
            },
            '& .MuiTab-root': isCompactLandscape ? {
              minHeight: 24,
              py: 0,
              px: 0.75,
              fontSize: '0.6rem',
            } : {},
          }}
        >
          <Tab label="Portfolio" icon={isMobile ? undefined : <AccountBalanceWalletIcon />} iconPosition="start" sx={sharedTabSx} />
          <Tab label="Dashboard" sx={sharedTabSx} />
          <Tab label={isMobile ? 'Financials' : 'Financial Statements'} sx={sharedTabSx} />
          <Tab label={isMobile ? 'Compare' : 'Compare Companies'} icon={isMobile ? undefined : <CompareArrowsIcon />} iconPosition="start" sx={sharedTabSx} />
          <Tab label="Metrics" sx={sharedTabSx} />
          <Tab label={isMobile ? 'Earnings' : 'Earnings Calendar'} sx={sharedTabSx} />
        </Tabs>
      )}

      {/* Sub-tabs for Crypto Analysis */}
      {mainTab === 3 && (
        <Tabs
          value={subTab}
          onChange={handleCryptoAnalysisTabChange}
          indicatorColor="secondary"
          textColor="secondary"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            mt: isCompactLandscape ? 0 : { xs: 1, sm: 2 },
            mb: isCompactLandscape ? 0 : 1,
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: isCompactLandscape ? 24 : { xs: 36, sm: 48 },
            '& .MuiTab-root': isCompactLandscape ? { minHeight: 24, py: 0, px: 0.75, fontSize: '0.6rem' } : {},
          }}
        >
          <Tab label="Coins" sx={sharedTabSx} />
          <Tab label="Dashboard" sx={sharedTabSx} />
          <Tab label={isMobile ? 'Market' : 'Market Data'} sx={sharedTabSx} />
          <Tab label="Compare" icon={isMobile ? undefined : <CompareArrowsIcon />} iconPosition="start" sx={sharedTabSx} />
        </Tabs>
      )}
      
      {/* Sub-tabs for Market News */}
      {mainTab === 1 && (
        <Tabs
          value={subTab}
          onChange={handleMarketNewsTabChange}
          indicatorColor="secondary"
          textColor="secondary"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            mt: isCompactLandscape ? 0 : { xs: 1, sm: 2 },
            mb: isCompactLandscape ? 0 : 1,
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: isCompactLandscape ? 24 : { xs: 36, sm: 48 },
            '& .MuiTab-root': isCompactLandscape ? { minHeight: 24, py: 0, px: 0.75, fontSize: '0.6rem' } : {},
          }}
        >
          <Tab label={isMobile ? 'Live' : 'Live News'} sx={sharedTabSx} />
          <Tab label={isMobile ? 'Calendar' : 'Economic Calendar'} sx={sharedTabSx} />
          <Tab label={isMobile ? 'Fed' : 'Fed Rates'} sx={sharedTabSx} />
          <Tab label={isMobile ? 'Money' : 'Money Supply'} sx={sharedTabSx} />
        </Tabs>
      )}
    </Box>
  );
};

const AppContent = ({
  selectedTicker,
  selectedExchange,
  handleTickerChange,
  dashboardTarget,
  handleWatchlistIndexSelect,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const showWatchlistSidebar =
    COMPANY_ANALYSIS_ROUTES.has(location.pathname) &&
    !WATCHLIST_SIDEBAR_HIDDEN_ROUTES.has(location.pathname);
  const showPaperAccountSummary = location.pathname === '/paper-trading';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        pt: { xs: 1.5, sm: 2, md: 2.5 },
        pb: { xs: 3, sm: 4 },
        '@media (max-height:480px) and (orientation:landscape)': { pt: 0.5 },
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 1.25, sm: 2, md: 3 }, mt: { xs: 1, sm: 2 }, mb: 0 }}>
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {showWatchlistSidebar && (
            <Grid item xs={12} md={3} sx={{ order: { xs: 2, md: 1 } }}>
              {showPaperAccountSummary && <PaperAccountSummary />}
              <Watchlist
                compact
                selectedExchange={selectedExchange}
                selectedTicker={selectedTicker}
                onSelectTicker={handleTickerChange}
                onSelectWatchlistIndex={handleWatchlistIndexSelect}
                watchlistIndexSelected={dashboardTarget?.type === 'watchlist'}
                navigateOnSelect={false}
                onManageWatchlists={() => navigate('/watchlist')}
              />
            </Grid>
          )}

          <Grid item xs={12} md={showWatchlistSidebar ? 9 : 12} sx={{ order: { xs: 1, md: 2 } }}>
            <Paper sx={{ p: { xs: 1.5, sm: 2, md: 2.5 }, overflow: 'hidden' }}>
              <Box>
                <Routes>
                  <Route 
                    path="/" 
                    element={<Navigate to="/learn/welcome" replace />} 
                  />
                  <Route 
                    path="/dashboard" 
                    element={
                      <CompanyDashboard 
                        ticker={selectedTicker} 
                        exchange={selectedExchange}
                        apiBaseUrl={API_CONFIG.API_BASE_URL}
                        watchlistIndex={dashboardTarget?.type === 'watchlist' ? dashboardTarget : null}
                      />
                    } 
                  />
                  <Route 
                    path="/learn" 
                      element={<LearnHub />} 
                  />
                    <Route 
                      path="/learn/video" 
                      element={<Navigate to="/learn/welcome" replace />} 
                    />
                    <Route 
                      path="/learn/welcome" 
                      element={<LearnHub />} 
                    />
                  <Route 
                    path="/financial-statements" 
                    element={
                      <RouteErrorBoundary>
                        <FinancialStatements 
                          ticker={selectedTicker} 
                          exchange={selectedExchange}
                          apiBaseUrl={API_CONFIG.API_BASE_URL}
                          visualizations={{
                            incomeStatement: 'sankey',
                            cashFlow: 'sankey',
                            balanceSheet: 'pie'
                          }}
                        />
                      </RouteErrorBoundary>
                    } 
                  />
                  <Route
                    path="/price-chart"
                    element={<Navigate to="/dashboard" replace />}
                  />
                  <Route 
                    path="/metric-table" 
                    element={
                      <MetricTable 
                        ticker={selectedTicker} 
                        exchange={selectedExchange}
                        apiBaseUrl={API_CONFIG.API_BASE_URL}
                      />
                    } 
                  />
                  <Route
                    path="/watchlist"
                    element={
                      <Watchlist
                        selectedExchange={selectedExchange}
                        selectedTicker={selectedTicker}
                        onSelectTicker={handleTickerChange}
                      />
                    }
                  />
                  <Route 
                    path="/crypto-analysis" 
                    element={<CryptoAnalysis view="dashboard" />}
                  />
                  <Route
                    path="/crypto-analysis/market"
                    element={<CryptoAnalysis view="market" />}
                  />
                  <Route
                    path="/crypto-analysis/coins"
                    element={<CryptoAnalysis view="coins" />}
                  />
                  <Route
                    path="/crypto-analysis/compare"
                    element={<CryptoAnalysis view="compare" />}
                  />
                  <Route 
                    path="/company-comparison" 
                    element={
                      <CompanyComparison 
                        initialTicker={selectedTicker} 
                      />
                    } 
                  />
                  <Route path="/CNBC-live" element={<CNBCLive />} />
                  <Route path="/visualization-guide" element={<VisualizationGuide />} />
                  <Route
                    path="/screener"
                    element={<Navigate to="/map" replace />}
                  />
                  <Route
                    path="/thesis"
                    element={<Navigate to="/paper-trading" replace />}
                  />
                  <Route
                    path="/alerts"
                    element={<Navigate to="/paper-trading" replace />}
                  />
                  <Route
                    path="/paper-trading"
                    element={<PaperTrading initialTicker={selectedTicker} />}
                  />
                  <Route
                    path="/account-settings"
                    element={<AccountSettings />}
                  />
                  <Route
                    path="/filings"
                    element={<Navigate to="/financial-statements" replace />}
                  />
                  <Route
                    path="/economic-calendar"
                    element={<EconomicCalendar />}
                  />
                  <Route
                    path="/fed-rates"
                    element={<FedRates />}
                  />
                  <Route
                    path="/money-supply"
                    element={<MoneySupply />}
                  />
                  <Route
                    path="/earnings-calendar"
                    element={
                      <EarningsCalendarTab
                        onSelectTicker={handleTickerChange}
                        defaultExchange={selectedExchange}
                      />
                    }
                  />
                  <Route path="/legal/terms" element={<TermsPage />} />
                  <Route path="/legal/privacy" element={<PrivacyPage />} />
                  <Route path="/legal/cookies" element={<CookiePolicyPage />} />
                </Routes>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 2,
            px: { xs: 0.5, sm: 1 },
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            justifyContent: { xs: 'center', md: 'flex-end' },
          }}
        >
          <Typography component={RouterLink} to="/legal/terms" sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            Terms
          </Typography>
          <Typography component={RouterLink} to="/legal/privacy" sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            Privacy
          </Typography>
          <Typography component={RouterLink} to="/legal/cookies" sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            Cookies
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

const AppFrame = ({
  selectedTicker,
  selectedExchange,
  handleTickerChange,
  dashboardTarget,
  handleWatchlistIndexSelect,
  toggleThemeMode,
  mode,
}) => {
  const location = useLocation();
  const isAccountSettingsRoute = location.pathname === '/account-settings';
  const isMapRoute = location.pathname === '/map';

  if (isAccountSettingsRoute) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          background: (theme) => `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 1.25, sm: 2, md: 3 }, py: { xs: 2, sm: 3, md: 4 } }}>
          <Paper sx={{ p: { xs: 1.5, sm: 2.5, md: 3 }, overflow: 'hidden' }}>
            <Routes>
              <Route path="/account-settings" element={<AccountSettings />} />
              <Route path="*" element={<Navigate to="/account-settings" replace />} />
            </Routes>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Full-page landing map (post-login default): no header chrome, just a large map.
  if (isMapRoute) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          background: (theme) => `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
        }}
      >
        <Container maxWidth={false} sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 1, sm: 1.5 } }}>
          <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 }, overflow: 'hidden', minHeight: 'calc(100vh - 24px)' }}>
            <CountryTickerMap onSelectTicker={handleTickerChange} fullPage />
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        minHeight: '100vh',
        background: (theme) => `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
      }}
    >
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{
          top: 0,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          borderBottom: 1,
          borderColor: 'divider',
          backdropFilter: 'blur(14px)',
          backgroundColor: (theme) => `${theme.palette.background.paper}f2`,
        }}
      >
        <Toolbar sx={{ px: { xs: 1, sm: 2 }, py: { xs: 0.25, sm: 1.25 }, minHeight: 'unset !important', alignItems: 'stretch', '@media (max-height:480px) and (orientation:landscape)': { py: 0 } }}>
          <Header 
            selectedTicker={selectedTicker}
            selectedExchange={selectedExchange}
            toggleTheme={toggleThemeMode}
            darkMode={mode === 'dark'}
            onTickerChange={handleTickerChange}
          />
        </Toolbar>
        <Box
          sx={{
            px: { xs: 0.75, sm: 2 },
            py: { xs: 0.125, sm: 0.75 },
            bgcolor: 'background.paper',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -1px rgba(0, 0, 0, 0.04)',
            '@media (max-height:480px) and (orientation:landscape)': { py: 0 },
          }}
        >
          <TabNavigator />
        </Box>
      </AppBar>

      <AppContent
        selectedTicker={selectedTicker}
        selectedExchange={selectedExchange}
        handleTickerChange={handleTickerChange}
        dashboardTarget={dashboardTarget}
        handleWatchlistIndexSelect={handleWatchlistIndexSelect}
      />
    </Box>
  );
};

function App() {
  const [selectedTicker, setSelectedTicker] = useState(
    () => localStorage.getItem(SELECTED_TICKER_STORAGE_KEY) || 'AAPL'
  );
  const [selectedExchange, setSelectedExchange] = useState(
    () => localStorage.getItem(SELECTED_EXCHANGE_STORAGE_KEY) || 'NASDAQ'
  );
  const [apiConnected, setApiConnected] = useState(false);
  const [mode, setMode] = useState(() => localStorage.getItem('themeMode') || 'light');
  const [dashboardTarget, setDashboardTarget] = useState({ type: 'ticker' });

  const theme = useMemo(() => getTheme(mode), [mode]);

  // Test API connection on component mount
  useEffect(() => {
    const testConnection = async () => {
      try {
        // Make a simple request to verify API is accessible
        const response = await fetch(`${API_CONFIG.API_BASE_URL}/api/health-check`);
        if (response.ok) {
          console.log('API connection successful');
          setApiConnected(true);
        } else {
          console.warn('API responded with non-200 status:', response.status);
        }
      } catch (error) {
        console.error('Failed to connect to API:', error);
      }
    };
    
    testConnection();
  }, []);

  const handleTickerChange = useCallback((ticker, exchange) => {
    if (ticker) {
      setSelectedTicker(ticker);
      setSelectedExchange(exchange);
      setDashboardTarget({ type: 'ticker' });
      localStorage.setItem(SELECTED_TICKER_STORAGE_KEY, ticker);
      localStorage.setItem(SELECTED_EXCHANGE_STORAGE_KEY, exchange);
    }
  }, []);

  const handleWatchlistIndexSelect = useCallback((watchlist) => {
    if (!watchlist || !Array.isArray(watchlist.tickers) || watchlist.tickers.length === 0) {
      return;
    }

    setDashboardTarget({
      type: 'watchlist',
      watchlistId: String(watchlist.id),
      name: watchlist.name || 'Watchlist',
      tickers: watchlist.tickers,
    });
  }, []);

  const toggleThemeMode = () => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', next);
      return next;
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <ProtectedRoute>
          <RouteErrorBoundary>
            <NavigationProvider>
              <AppFrame
                selectedTicker={selectedTicker}
                selectedExchange={selectedExchange}
                handleTickerChange={handleTickerChange}
                dashboardTarget={dashboardTarget}
                handleWatchlistIndexSelect={handleWatchlistIndexSelect}
                toggleThemeMode={toggleThemeMode}
                mode={mode}
              />
            </NavigationProvider>
          </RouteErrorBoundary>
        </ProtectedRoute>
      </Router>
    </ThemeProvider>
  );
}

export default App;